import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, FileSignature, ShieldOff, X, Clock, PhoneCall, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "../../components/PortalLayout";
import { api, formatApiError } from "../../lib/api";
import { ResumeReviewCard } from "../../components/ResumeReviewCard";
import { CompleteIntakeModal } from "../../components/CompleteIntakeModal";
import { FoundEmploymentDialog } from "../../components/FoundEmploymentDialog";
import { useAuth } from "../../context/AuthContext";

const TASK_LABELS = {
  personal_details: "Complete personal details",
  work_rights: "Add work rights / visa info",
  cv: "Upload your CV",
  photo: "Upload a profile photo",
  credentials: "Upload credentials",
  references: "Upload references",
  id: "Upload photo ID",
  police_check: "Upload police check",
  consent: "Sign consent form",
  submitted: "Submit profile for review",
};

export default function CandidateDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [blockInput, setBlockInput] = useState("");
  const [builderReq, setBuilderReq] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [intakeDismissed, setIntakeDismissed] = useState(false);
  const [foundOpen, setFoundOpen] = useState(false);

  useEffect(() => {
    api.get("/candidate/dashboard").then((res) => {
      setData(res.data);
      setBlocked(res.data.profile.blocked_companies || []);
    }).catch(() => {});
    api.get("/candidate/cv-builder").then((r) => setBuilderReq(r.data)).catch(() => {});
  }, []);

  const reloadDashboard = async () => {
    try {
      const res = await api.get("/candidate/dashboard");
      setData(res.data);
      setBlocked(res.data.profile.blocked_companies || []);
    } catch { /* noop */ }
  };

  const recheckAccess = async () => {
    setRechecking(true);
    try {
      const { data: r } = await api.post("/candidate/recheck-access");
      if (r.careers_access) {
        toast.success("Access unlocked — welcome aboard!");
        window.location.reload();
      } else {
        toast.error("No package purchase found for your email yet. If you've just purchased, give it a minute and try again.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRechecking(false);
    }
  };

  const saveBlocked = async (list) => {
    setBlocked(list);
    try {
      await api.put("/candidate/profile", { blocked_companies: list });
      toast.success("Privacy preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addBlocked = () => {
    const name = blockInput.trim();
    if (!name) return;
    if (blocked.some((b) => b.toLowerCase() === name.toLowerCase())) return setBlockInput("");
    saveBlocked([...blocked, name]);
    setBlockInput("");
  };

  if (!data)
    return (
      <PortalLayout>
        <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-[#0d2f4d] border-t-transparent animate-spin" /></div>
      </PortalLayout>
    );

  const { completion, profile, documents, consents } = data;
  const pending = Object.entries(completion.checks).filter(([, done]) => !done);
  const needsIntake = profile.source_form !== "candidate_profile_form";

  return (
    <PortalLayout>
      <div data-testid="candidate-dashboard">
        <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">Candidate dashboard</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">
          Kia ora, {user?.first_name} 👋
        </h1>
        <p className="text-[#51637a] mt-1 text-sm">
          {profile.submitted
            ? "Your profile is submitted and awaiting recruiter review."
            : "Complete your profile so our recruiters can start matching you."}
        </p>

        {needsIntake && !intakeDismissed && (
          <div className="mt-6 flex flex-col gap-4 rounded-lg border border-[#C1CC24] bg-[#f4f6e0] p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="intake-nudge-banner">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0d2f4d] text-[#C1CC24]">
                <FileSignature size={17} />
              </span>
              <div>
                <h2 className="font-heading font-bold text-[#0d2f4d]">We've updated our candidate profile</h2>
                <p className="text-sm text-[#51637a] mt-1 max-w-xl leading-relaxed">
                  Please take a couple of minutes to answer a few new questions so our recruiters can represent you properly.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button onClick={() => setIntakeOpen(true)} data-testid="open-intake-modal"
                className="inline-flex items-center gap-2 rounded-full bg-[#0d2f4d] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1c4d78] transition-colors">
                Complete now <ArrowRight size={15} />
              </button>
              <button onClick={() => setIntakeDismissed(true)} data-testid="dismiss-intake-banner"
                className="rounded-full p-2 text-[#51637a] hover:bg-[#e3e0d3]" aria-label="Dismiss"><X size={16} /></button>
            </div>
          </div>
        )}

        {intakeOpen && (
          <CompleteIntakeModal
            initial={{ mobile: profile.mobile, location: profile.location }}
            onClose={() => setIntakeOpen(false)}
            onDone={async () => { setIntakeOpen(false); await reloadDashboard(); }}
          />
        )}

        {profile.careers_access === false && (
          <div className="mt-6 bg-[#0d2f4d] rounded-lg grain relative overflow-hidden p-8 sm:p-10" data-testid="careers-locked-card">
            <p className="relative flex items-center gap-2 font-accent text-lg text-[#C1CC24]"><ShieldOff size={18} /> Careers access locked</p>
            <h2 className="relative text-xl sm:text-2xl font-bold text-white tracking-tight mt-2 max-w-xl">
              Careers access is part of your relocation packages — choose one to get started.
            </h2>
            <p className="relative text-sm text-white/70 mt-3 max-w-xl leading-relaxed">
              Since you're still overseas, your careers profile unlocks with any of our relocation
              package — Landing, Settlement, Full Concierge or Family Move. Already purchased? Hit re-check
              and we'll find it against your email.
            </p>
            <div className="relative mt-6 flex flex-col sm:flex-row gap-3">
              <a href={data.packages_url || "#"} target="_blank" rel="noreferrer" data-testid="choose-package-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-semibold text-sm px-7 py-3.5 hover:bg-[#aeb81f] transition-colors">
                Choose my package <ArrowRight size={15} />
              </a>
              <button onClick={recheckAccess} disabled={rechecking} data-testid="recheck-access-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/50 text-white font-medium text-sm px-7 py-3.5 hover:bg-white hover:text-[#0d2f4d] transition-colors disabled:opacity-60">
                {rechecking ? "Checking..." : "I've purchased — check again"}
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="ditch-card p-6 md:col-span-2" data-testid="profile-completion-card">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-[#0d2f4d]">Profile completion</h2>
              <span className="font-heading font-extrabold text-2xl text-[#7C8514]" data-testid="completion-percent">{completion.percent}%</span>
            </div>
            <div className="mt-4 h-2.5 rounded-full bg-[#f5f3ee] overflow-hidden">
              <div className="h-full rounded-full bg-[#C1CC24] transition-[width] duration-700" style={{ width: `${completion.percent}%` }} />
            </div>
            <Link to="/candidate/onboarding" data-testid="continue-profile-cta"
              className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-6 py-3 hover:bg-[#1c4d78] transition-colors">
              {completion.percent >= 100 ? "Review my profile" : "Continue my profile"} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="ditch-card p-6" data-testid="status-card">
            <h2 className="font-heading font-bold text-[#0d2f4d]">Status</h2>
            <p className="mt-3 inline-flex rounded-full bg-[#f5f3ee] text-[#0d2f4d] text-xs font-medium px-3 py-1.5 capitalize">
              {profile.candidate_status?.replace("_", " ") || "new"}
            </p>
            <p className="text-xs text-[#51637a] mt-4 leading-relaxed">
              {profile.submitted
                ? "A recruiter will review your profile and be in touch. No action needed."
                : "Finish the checklist to submit your profile for recruiter review."}
            </p>
          </div>
        </div>

        {profile.submitted && (
          <div className="ditch-card p-6 mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="candidate-book-call-card">
            <div>
              <h2 className="font-heading font-bold text-[#0d2f4d]">Ready to chat? Book a call with a recruiter</h2>
              <p className="text-sm text-[#51637a] mt-1">Your profile is in — grab a time that suits and we'll talk through your next move.</p>
            </div>
            <a href="https://calendly.com/d/dtgj-6kj-rpp/careers-call-candidate" target="_blank" rel="noreferrer" data-testid="candidate-calendly-link"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium text-sm px-6 py-3 hover:bg-[#aeb81f] transition-colors">
              <PhoneCall size={15} /> Book my call
            </a>
          </div>
        )}

        {profile.found_employment ? (
          <div className="ditch-card p-6 mt-4 flex items-start gap-4" data-testid="placed-card">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C1CC24]/25 text-[#7C8514]"><PartyPopper size={20} /></span>
            <div>
              <h2 className="font-heading font-bold text-[#0d2f4d]">Congratulations on the new role! 🎉</h2>
              <p className="text-sm text-[#51637a] mt-1">Thanks for letting us know — we've updated your file. If anything changes, just reach out.</p>
            </div>
          </div>
        ) : (
          <div className="ditch-card p-6 mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="found-employment-card">
            <div>
              <h2 className="font-heading font-bold text-[#0d2f4d]">Found a job?</h2>
              <p className="text-sm text-[#51637a] mt-1">Let us know so we can update your file and stop matching you to new roles.</p>
            </div>
            <button onClick={() => setFoundOpen(true)} data-testid="found-employment-button"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#0d2f4d] text-white font-medium text-sm px-6 py-3 hover:bg-[#1c4d78] transition-colors">
              <PartyPopper size={15} /> I've found employment
            </button>
          </div>
        )}

        {foundOpen && (
          <FoundEmploymentDialog
            onClose={() => setFoundOpen(false)}
            onDone={async () => { setFoundOpen(false); await reloadDashboard(); }}
          />
        )}

        {profile.careers_access !== false && (
        <div className="mt-4">
          {builderReq && ["in_progress", "awaiting_signoff", "completed"].includes(builderReq.status) ? (
            <div className="ditch-card p-6 flex items-start gap-4" data-testid="cv-crafting-status-card">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C1CC24]/20">
                <Clock size={20} className="text-[#7C8514]" />
              </span>
              <div>
                <h2 className="font-heading font-bold text-[#0d2f4d]">
                  {builderReq.status !== "completed" ? "Your CV is being crafted — ready within 48 hours" : "Your curated CV is ready"}
                </h2>
                <p className="text-sm text-[#51637a] mt-1 leading-relaxed">
                  {builderReq.status !== "completed"
                    ? "Our team is building your market-ready CV right now. We'll be in touch as soon as it's done — no action needed from you."
                    : "Your professionally curated CV is done — check your documents or your inbox."}
                </p>
              </div>
            </div>
          ) : (
            <ResumeReviewCard testId="dashboard-resume-review-card" onAction={() => navigate("/candidate/onboarding?step=0")} />
          )}
        </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="ditch-card p-6" data-testid="tasks-card">
            <h2 className="font-heading font-bold text-[#0d2f4d]">Required actions</h2>
            {pending.length === 0 ? (
              <p className="text-sm text-[#51637a] mt-4">All done — nice work. Your profile is complete.</p>
            ) : (
              <ul className="mt-4 space-y-2.5">
                {pending.slice(0, 6).map(([key]) => (
                  <li key={key} className="flex items-center gap-3 text-sm text-[#122c47]">
                    <Circle size={15} className="text-[#e3e0d3]" /> {TASK_LABELS[key]}
                  </li>
                ))}
              </ul>
            )}
            {Object.entries(completion.checks).filter(([, d]) => d).slice(0, 4).map(([key]) => (
              <p key={key} className="flex items-center gap-3 text-sm text-[#51637a] mt-2.5">
                <CheckCircle2 size={15} className="text-emerald-600" /> {TASK_LABELS[key]}
              </p>
            ))}
          </div>
          <div className="ditch-card p-6" data-testid="uploads-summary-card">
            <h2 className="font-heading font-bold text-[#0d2f4d]">Uploads & consent</h2>
            <p className="text-sm text-[#51637a] mt-3">{documents.length} document{documents.length === 1 ? "" : "s"} uploaded</p>
            <p className="flex items-center gap-2 text-sm mt-2 text-[#51637a]">
              <FileSignature size={15} className={consents.length ? "text-emerald-600" : "text-[#e3e0d3]"} />
              {consents.length ? `Consent signed by ${consents[0].typed_signature}` : "Consent not yet signed"}
            </p>
            <Link to="/candidate/documents" data-testid="go-to-documents-cta"
              className="inline-flex items-center gap-2 mt-5 text-sm text-[#7C8514] font-medium hover:gap-3 transition-all">
              Go to upload centre <ArrowRight size={15} />
            </Link>
          </div>
        </div>
        <div className="ditch-card p-6 mt-4" data-testid="privacy-card">
          <h2 className="font-heading font-bold text-[#0d2f4d] flex items-center gap-2"><ShieldOff size={17} /> Choose who can't see you</h2>
          <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
            Add companies you don't want your profile shared with — like your current employer.
            This stays private: they'll simply never see you in a shortlist.
          </p>
          <div className="mt-4 flex gap-2 max-w-md">
            <input value={blockInput} onChange={(e) => setBlockInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addBlocked()}
              placeholder="Company name" data-testid="blocked-company-input"
              className="flex-1 rounded-lg border border-[#e3e0d3] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]" />
            <button onClick={addBlocked} data-testid="blocked-company-add"
              className="rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#1c4d78] transition-colors">
              Add
            </button>
          </div>
          {blocked.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="blocked-company-list">
              {blocked.map((b) => (
                <span key={b} className="inline-flex items-center gap-2 rounded-full bg-[#f5f3ee] border border-[#e3e0d3] text-sm text-[#0d2f4d] px-3.5 py-1.5">
                  {b}
                  <button onClick={() => saveBlocked(blocked.filter((x) => x !== b))}
                    data-testid={`blocked-remove-${b.toLowerCase().replace(/ /g, "-")}`}
                    className="text-[#51637a] hover:text-red-600 transition-colors"><X size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
